import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  X,
  Search,
  Book,
  HelpCircle,
  ChevronRight,
  ExternalLink,
  Lightbulb,
  FileText,
  Video,
  ArrowRight,
} from 'lucide-react';
import {
  usePageDocumentation,
  usePageTips,
  useDocumentationSearch,
} from '../../hooks/useDocumentation';
import { getCategoryConfig } from '../../types/documentation';

interface HelpPanelProps {
  pageRoute: string;
  onClose: () => void;
}

export const HelpPanel: React.FC<HelpPanelProps> = ({ pageRoute, onClose }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [activeTab, setActiveTab] = useState<'tips' | 'articles' | 'search'>('tips');

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      if (searchQuery.length >= 2) {
        setActiveTab('search');
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Queries
  const { data: pageArticles, isLoading: articlesLoading } = usePageDocumentation(pageRoute);
  const { data: pageTips, isLoading: tipsLoading } = usePageTips(pageRoute);
  const { data: searchResults, isLoading: searchLoading } = useDocumentationSearch(debouncedSearch);

  // Navigate to documentation page with article
  const handleArticleClick = (slug: string) => {
    navigate(`/documentation?article=${slug}`);
    onClose();
  };

  // Navigate to full documentation
  const handleViewAll = () => {
    navigate('/documentation');
    onClose();
  };

  // Get page name from route
  const getPageName = (route: string): string => {
    const parts = route.split('/').filter(Boolean);
    if (parts.length === 0) return 'Dashboard';
    return parts[parts.length - 1]
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <motion.div
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{ type: 'spring', damping: 25, stiffness: 200 }}
      className="fixed right-0 top-0 h-full w-96 bg-white shadow-xl z-50 flex flex-col"
    >
      {/* Header */}
      <div className="p-4 border-b bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <HelpCircle className="h-6 w-6" />
            <h2 className="text-lg font-semibold">Help & Tips</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-white/20 rounded transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-blue-200" />
          <input
            type="text"
            placeholder="Search documentation..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-blue-200 focus:bg-white/20 focus:border-white/40 transition-all"
          />
        </div>

        {/* Current page indicator */}
        <div className="mt-3 text-sm text-blue-100">
          Showing help for: <span className="font-medium">{getPageName(pageRoute)}</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b">
        <button
          onClick={() => setActiveTab('tips')}
          className={`flex-1 py-3 text-sm font-medium transition-colors ${
            activeTab === 'tips'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <div className="flex items-center justify-center gap-1">
            <Lightbulb className="h-4 w-4" />
            Tips
            {pageTips && pageTips.length > 0 && (
              <span className="ml-1 bg-blue-100 text-blue-600 text-xs px-1.5 rounded-full">
                {pageTips.length}
              </span>
            )}
          </div>
        </button>
        <button
          onClick={() => setActiveTab('articles')}
          className={`flex-1 py-3 text-sm font-medium transition-colors ${
            activeTab === 'articles'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <div className="flex items-center justify-center gap-1">
            <FileText className="h-4 w-4" />
            Articles
            {pageArticles && pageArticles.length > 0 && (
              <span className="ml-1 bg-blue-100 text-blue-600 text-xs px-1.5 rounded-full">
                {pageArticles.length}
              </span>
            )}
          </div>
        </button>
        {debouncedSearch.length >= 2 && (
          <button
            onClick={() => setActiveTab('search')}
            className={`flex-1 py-3 text-sm font-medium transition-colors ${
              activeTab === 'search'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center justify-center gap-1">
              <Search className="h-4 w-4" />
              Results
              {searchResults && (
                <span className="ml-1 bg-blue-100 text-blue-600 text-xs px-1.5 rounded-full">
                  {searchResults.length}
                </span>
              )}
            </div>
          </button>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Tips Tab */}
        {activeTab === 'tips' && (
          <div className="space-y-3">
            {tipsLoading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-20 bg-gray-100 rounded-lg animate-pulse" />
                ))}
              </div>
            ) : pageTips && pageTips.length > 0 ? (
              pageTips.map((tip) => (
                <div
                  key={tip.id}
                  className="p-4 bg-amber-50 border border-amber-200 rounded-lg"
                >
                  <div className="flex items-start gap-3">
                    <Lightbulb className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-amber-800">{tip.title}</h4>
                      <p className="text-sm text-amber-700 mt-1">{tip.content}</p>
                      {tip.learnMoreSlug && (
                        <button
                          onClick={() => handleArticleClick(tip.learnMoreSlug!)}
                          className="text-sm text-amber-600 hover:text-amber-700 mt-2 inline-flex items-center gap-1"
                        >
                          Learn more <ArrowRight className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Lightbulb className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                <p>No tips available for this page yet.</p>
                <button
                  onClick={() => setActiveTab('articles')}
                  className="text-blue-600 hover:text-blue-700 mt-2 text-sm"
                >
                  View related articles instead
                </button>
              </div>
            )}
          </div>
        )}

        {/* Articles Tab */}
        {activeTab === 'articles' && (
          <div className="space-y-3">
            {articlesLoading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-20 bg-gray-100 rounded-lg animate-pulse" />
                ))}
              </div>
            ) : pageArticles && pageArticles.length > 0 ? (
              pageArticles.map((article) => (
                <button
                  key={article.id}
                  onClick={() => handleArticleClick(article.slug)}
                  className="w-full p-4 bg-white border border-gray-200 rounded-lg text-left hover:border-blue-300 hover:bg-blue-50 transition-all group"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900 group-hover:text-blue-600">
                        {article.title}
                      </h4>
                      {article.summary && (
                        <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                          {article.summary}
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                          {getCategoryConfig(article.category).label}
                        </span>
                        {article.videoUrl && (
                          <span className="text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded flex items-center gap-1">
                            <Video className="h-3 w-3" />
                            Video
                          </span>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-blue-500 flex-shrink-0" />
                  </div>
                </button>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <FileText className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                <p>No articles for this specific page.</p>
                <button
                  onClick={handleViewAll}
                  className="text-blue-600 hover:text-blue-700 mt-2 text-sm"
                >
                  Browse all documentation
                </button>
              </div>
            )}
          </div>
        )}

        {/* Search Results Tab */}
        {activeTab === 'search' && (
          <div className="space-y-3">
            {searchLoading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-20 bg-gray-100 rounded-lg animate-pulse" />
                ))}
              </div>
            ) : searchResults && searchResults.length > 0 ? (
              <>
                <p className="text-sm text-gray-500 mb-2">
                  Found {searchResults.length} results for "{debouncedSearch}"
                </p>
                {searchResults.map((article) => (
                  <button
                    key={article.id}
                    onClick={() => handleArticleClick(article.slug)}
                    className="w-full p-4 bg-white border border-gray-200 rounded-lg text-left hover:border-blue-300 hover:bg-blue-50 transition-all group"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 group-hover:text-blue-600">
                          {article.title}
                        </h4>
                        {article.summary && (
                          <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                            {article.summary}
                          </p>
                        )}
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded mt-2 inline-block">
                          {getCategoryConfig(article.category).label}
                        </span>
                      </div>
                      <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-blue-500 flex-shrink-0" />
                    </div>
                  </button>
                ))}
              </>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Search className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                <p>No results found for "{debouncedSearch}"</p>
                <p className="text-sm mt-1">Try different keywords</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 border-t bg-gray-50">
        <button
          onClick={handleViewAll}
          className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
        >
          <Book className="h-4 w-4" />
          View Full Documentation
          <ExternalLink className="h-4 w-4" />
        </button>
        <p className="text-xs text-center text-gray-400 mt-2">
          Press <kbd className="px-1.5 py-0.5 bg-gray-200 rounded text-gray-600">Ctrl + /</kbd> to toggle
        </p>
      </div>
    </motion.div>
  );
};
